import {
  FormFieldType,
  type FormField,
  type FormPrimitiveValues,
  type FormPrimitiveValuesArray,
  type FormPrimitiveValuesObject,
} from '@/forms/typings';
import { adapt12hoursTimeTo24hoursTime } from '../../utilities';
import { FormFieldValueDTO } from '@/api/base/DTOs';
import { config } from '@/forms/config';
import type {
  IndexedExternalLookups,
  IndexedInternalLookups,
} from '@/typings';

export type SectionFieldLookupSnapshot = {
  internalLookups: IndexedInternalLookups;
  externalLookups: IndexedExternalLookups;
};

export type MapPrimitiveToFormFieldValueContext = {
  formFieldType: FormField['type'];
  useShortName: boolean;
  fieldId: string;
  isMasked: boolean;
  lookups: SectionFieldLookupSnapshot;
};

/**
 * Maps Formik field name to API itemID. Office-hours on-call suffix is always stripped.
 * When the field value is omitted (`undefined`), the question-with-details suffix is kept.
 */
export const resolveSectionFormFieldItemId = (
  fieldId: string,
  hasFieldValue: boolean,
): string => {
  if (fieldId.endsWith(config.officeHoursOnCallFieldSuffix)) {
    return fieldId.replace(config.officeHoursOnCallFieldSuffix, '');
  }
  if (hasFieldValue) {
    return fieldId.replace(config.questionWithDetailsFieldSuffix, '');
  }
  return fieldId;
};

/** Reads masked input raw value from DOM (legacy coupling for IMask fields). */
const readUnmaskedInputValue = (fieldId: string): string => {
  const field = document.getElementsByName(fieldId)?.[0] as
    | HTMLInputElement
    | undefined;
  return field?.getAttribute('data-unmasked') ?? '';
};

const isFormPrimitive = (v: unknown): v is FormPrimitiveValues =>
  typeof v === 'string' ||
  typeof v === 'number' ||
  typeof v === 'boolean';

/**
 * Normalizes Formik values into a list of primitives sent as `values` on the section update DTO.
 * Object arrays (unsupported for this API path) yield an empty list.
 */
export const expandFieldValuesToPrimitives = (
  fieldValues:
    | FormPrimitiveValues
    | FormPrimitiveValuesArray
    | FormPrimitiveValuesObject[],
): FormPrimitiveValues[] => {
  if (Array.isArray(fieldValues)) {
    if (!fieldValues.every(isFormPrimitive)) {
      return [];
    }
    return fieldValues;
  }
  if (
    typeof fieldValues === 'string' &&
    fieldValues.includes(config.multiValueDivider)
  ) {
    return fieldValues.split(config.multiValueDivider);
  }
  return [fieldValues];
};

const mapStringByFieldType = (
  fieldValues: string,
  formFieldType: FormField['type'],
): FormFieldValueDTO | undefined => {
  switch (formFieldType) {
    case FormFieldType.Date:
      return fieldValues === ''
        ? { value: fieldValues }
        : { valueDate: fieldValues };
    case FormFieldType.Time:
      return {
        valueTime:
          fieldValues === ''
            ? undefined
            : `${adapt12hoursTimeTo24hoursTime(fieldValues)}:00`,
      };
    default:
      return undefined;
  }
};

const mapInternalLookupString = (
  fieldValues: string,
  useShortName: boolean,
  internalLookups: IndexedInternalLookups,
): FormFieldValueDTO | undefined => {
  if (!fieldValues.startsWith(config.internalLookupPrefix)) {
    return undefined;
  }
  const [, lookupTypeId, lookupId] = fieldValues.split(
    config.internalLookupPrefix,
  );
  const lookup = internalLookups[Number(lookupTypeId)].find(
    l => l.id === lookupId,
  );
  return {
    value: (useShortName ? lookup?.shortName : lookup?.name) || '',
    lookupID: lookupId,
  };
};

const mapExternalLookupString = (
  fieldValues: string,
  fieldId: string,
  useShortName: boolean,
  externalLookups: IndexedExternalLookups,
): FormFieldValueDTO | undefined => {
  if (!fieldValues.startsWith(config.externalLookupPrefix)) {
    return undefined;
  }
  const value = fieldValues.replace(config.externalLookupPrefix, '');
  const lookup = externalLookups[fieldId].values.find(l => l.id === value);
  return {
    value: (useShortName ? lookup?.shortName : lookup?.name) || '',
    lookupID: value,
  };
};

const mapQuestionWithDetailsString = (
  fieldValues: string,
  fieldId: string,
): FormFieldValueDTO | undefined => {
  if (!fieldId.endsWith(config.questionWithDetailsFieldSuffix)) {
    return undefined;
  }
  return fieldValues === ''
    ? { value: fieldValues }
    : { valueBool: fieldValues === 'yes' };
};

const mapPlainString = (
  fieldValues: string,
  fieldId: string,
  isMasked: boolean,
): FormFieldValueDTO => {
  if (isMasked) {
    return { value: readUnmaskedInputValue(fieldId) };
  }
  return { value: fieldValues };
};

const mapStringToFormFieldValueDto = (
  fieldValues: string,
  ctx: MapPrimitiveToFormFieldValueContext,
): FormFieldValueDTO => {
  const byType = mapStringByFieldType(fieldValues, ctx.formFieldType);
  if (byType !== undefined) {
    return byType;
  }

  return (
    mapInternalLookupString(
      fieldValues,
      ctx.useShortName,
      ctx.lookups.internalLookups,
    ) ??
    mapExternalLookupString(
      fieldValues,
      ctx.fieldId,
      ctx.useShortName,
      ctx.lookups.externalLookups,
    ) ??
    mapQuestionWithDetailsString(fieldValues, ctx.fieldId) ??
    mapPlainString(fieldValues, ctx.fieldId, ctx.isMasked)
  );
};

export const mapPrimitiveToFormFieldValueDto = (
  fieldValues: FormPrimitiveValues,
  ctx: MapPrimitiveToFormFieldValueContext,
): FormFieldValueDTO => {
  switch (typeof fieldValues) {
    case 'string':
      return mapStringToFormFieldValueDto(fieldValues, ctx);
    case 'number':
      return { valueNumber: fieldValues };
    case 'boolean':
      return { valueBool: fieldValues };
    default: {
      const _exhaustive: never = fieldValues;
      return _exhaustive;
    }
  }
};
